import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type ServiceClient = {
  id: string;
  name: string;
  scopes: string[];
  status: string;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
};

export type CreatedServiceClient = Pick<ServiceClient, 'id' | 'name' | 'scopes'> & {
  /** Returned by the server once and never persisted in clear text. */
  secret: string;
  /** Single-header form of the credential, also returned only once. */
  apiKey?: string;
};

export type CreateServiceClientInput = { name: string; scopes: string[] };

const LIST_KEY = ['service-clients'] as const;

export function useServiceClients(enabled = true) {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<ServiceClient[]> => {
      const response = await apiClient.get<ServiceClient[]>('/service-clients');
      return response.data;
    },
    enabled,
  });
}

export function useCreateServiceClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateServiceClientInput): Promise<CreatedServiceClient> => {
      const response = await apiClient.post<CreatedServiceClient>('/service-clients', {
        name: input.name,
        scopes: input.scopes,
      });
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useRevokeServiceClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<ServiceClient> => {
      const response = await apiClient.post<ServiceClient>(`/service-clients/${id}/revoke`);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  });
}
